import { useMutation } from "@tanstack/react-query";
import { useId, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { post } from "@/lib/api";
import { centsEur } from "@/lib/documents";
import { parseNumber, today } from "@/lib/format";
import type { Approval, DocumentDetail } from "@/lib/types";
import { cn } from "@/lib/utils";
import { toCents } from "../../../invoicing/money.ts";
import { ApprovalFooter, ApprovalNoticeLine, askedAt } from "./ApprovalBanner";
import { useApprovalDecision, useRefreshDocument } from "./useDocument";

const amountText = (cents: number) => new Intl.NumberFormat("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(cents / 100);

/**
 * „Zahlung erfassen“: amount (the open amount) and date (today). With `approval` the agent's booking, read-only, to
 * approve or reject. `onBooked` runs once the payment is in, so its row can flash.
 */
export function PaymentDialog({
  detail, approval, onClose, onBooked,
}: {
  detail: DocumentDetail;
  approval?: Approval;
  onClose: () => void;
  onBooked: () => void;
}) {
  const doc = detail.document;
  const ids = useId();
  const refresh = useRefreshDocument(doc.id);
  const decision = useApprovalDecision({ id: approval?.id ?? 0, documentId: doc.id });
  const openCents = doc.totalCents - doc.paidCents;
  const asked = approval?.args;
  const [amount, setAmount] = useState(() => amountText(typeof asked?.amount === "number" ? toCents(asked.amount) : openCents));
  const [day, setDay] = useState(() => (typeof asked?.date === "string" ? asked.date : today()));
  const parsed = parseNumber(amount);
  const invalid = parsed === null || !(parsed > 0) ? "Bitte einen Betrag eingeben" : toCents(parsed) > openCents ? `Höchstens ${centsEur(openCents)} sind offen` : null;

  const book = useMutation({
    mutationFn: () => post<{ status: "done" | "pending" }>(`/api/documents/${doc.id}/payments`, { amount: parsed, date: day }),
    onSuccess: (r) => {
      onClose();
      if (r.status === "pending") {
        toast.info("Wartet auf Freigabe");
        return;
      }
      onBooked();
    },
    onSettled: refresh,
  });
  const done = () => {
    onClose();
    onBooked();
  };

  const error = book.error?.message ?? decision.approve.error?.message ?? null;
  const readOnly = approval !== undefined;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="pr-6 text-left">
          {approval && <p className="text-[0.8125rem] text-muted-foreground">{approval.title} · {askedAt(approval.requestedAt)}</p>}
          <DialogTitle>Zahlung erfassen</DialogTitle>
          <DialogDescription>
            {doc.number} · offen {centsEur(openCents)} von {centsEur(doc.totalCents)}
          </DialogDescription>
        </DialogHeader>
        <form
          className="contents"
          onSubmit={(e) => {
            e.preventDefault();
            if (!readOnly && !invalid && !book.isPending) book.mutate();
          }}
        >
          <div className="grid grid-cols-2 gap-3">
            <div className="flex min-w-0 flex-col gap-1.5">
              <Label htmlFor={`${ids}-amount`}>Betrag</Label>
              <div className="relative">
                <Input
                  id={`${ids}-amount`}
                  inputMode="decimal"
                  autoComplete="off"
                  value={amount}
                  readOnly={readOnly}
                  onChange={(e) => setAmount(e.target.value)}
                  aria-invalid={(!readOnly && invalid !== null) || undefined}
                  aria-describedby={`${ids}-amount-error`}
                  className="pr-7 text-right tabular-nums pointer-coarse:h-11"
                />
                <span aria-hidden className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center text-sm text-muted-foreground">€</span>
              </div>
            </div>
            <div className="flex min-w-0 flex-col gap-1.5">
              <Label htmlFor={`${ids}-date`}>Datum</Label>
              <Input id={`${ids}-date`} type="date" value={day} readOnly={readOnly} onChange={(e) => setDay(e.target.value)} className="pointer-coarse:h-11" />
            </div>
          </div>
          {/* What will be booked, as parsed: „1.000“ is a thousand euros, „2.5“ two and a half. */}
          <p id={`${ids}-amount-error`} aria-live="polite" className={cn("-mt-1 min-h-5 text-[0.8125rem] tabular-nums", invalid ? "text-destructive" : "text-muted-foreground")}>
            {amount.trim() === "" ? "" : invalid && !readOnly ? invalid : parsed !== null ? `= ${centsEur(toCents(parsed))}` : ""}
          </p>
          {approval && typeof approval.args.note === "string" && approval.args.note.trim() !== "" && (
            <p className="text-[0.8125rem]"><span className="text-muted-foreground">Notiz vom Agent: </span>{approval.args.note}</p>
          )}
          {approval && <ApprovalNoticeLine approval={approval} number={doc.number} />}
          {error && <p role="alert" className="text-[0.8125rem] text-destructive">{error}</p>}
          {approval ? (
            <ApprovalFooter
              approval={approval}
              primary="Freigeben & buchen"
              disabled={decision.approve.isPending}
              onApprove={() => decision.approve.mutate({}, { onSuccess: done })}
              onClose={onClose}
            />
          ) : (
            <DialogFooter>
              <Button type="button" variant="ghost" className="pointer-coarse:h-11" onClick={onClose}>Abbrechen</Button>
              <Button type="submit" className="pointer-coarse:h-11" disabled={invalid !== null || !day || book.isPending}>Zahlung buchen</Button>
            </DialogFooter>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
}
